import styles from './SapRfcConnection.module.css';

import Card, { CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/Card/Card';
import { useNavigate, useParams } from 'react-router-dom';
import { useBackNavigation } from '../../../hooks/useBackNavigation';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from '../../../lib/toast';
import { configApi } from '../../../lib/apiClient';
import { useAuthStore } from '../../../store/authStore';
import { useConnectionsStore } from '../../../store/connectionsStore';
import { testConnectionApi } from '../../../lib/testConnection';
import SecureInput from '../../../ui/SecureInput/SecureInput';
import { UserRole } from '../../../enums/userRole';
import { hasMinRole } from '../../../lib/permissions';

const sapRfcSchema = z.object({
    name: z.string().min(1, 'Connection name is required'),
    environment: z.enum(['test', 'prod', 'dev']).optional(),
    ashost: z.string().min(1, 'Application server host is required'),
    sysnr: z.string().regex(/^\d{2}$/, 'System number must be exactly 2 digits (e.g. 00)'),
    client: z.string().regex(/^\d{3}$/, 'Client must be exactly 3 digits (e.g. 800)'),
    lang: z.string().min(1).max(2).default('EN'),
    user: z.string().min(1, 'Username is required'),
    password: z.string().min(1, 'Password is required'),
    poolMax: z.coerce.number().int().min(1).max(100).default(10),
});

type SapRfcForm = z.infer<typeof sapRfcSchema>;

export default function SapRfcConnection() {
    const navigate = useNavigate();
    const goBack = useBackNavigation('/connectors');
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [config, setConfig] = useState<any | null>(null);
    const user = useAuthStore(state => state.user);
    const { connectors, updateConnection } = useConnectionsStore(state => state);
    const { configId } = useParams();

    const { handleSubmit, setValue, watch, formState: { errors }, setError } = useForm<SapRfcForm>({
        resolver: zodResolver(sapRfcSchema),
        defaultValues: {
            name: '',
            environment: 'prod',
            ashost: '',
            sysnr: '00',
            client: '800',
            lang: 'EN',
            user: '',
            password: '',
            poolMax: 10,
        },
    });

    useEffect(() => {
        if (!user || !hasMinRole(user?.role?.id, UserRole.Admin)) {
            navigate('/');
        }
    }, [user]);

    useEffect(() => {
        const configData = connectors.find(c => c.id === configId) || null;
        if (configData) {
            setConfig(configData);
            setValue('name', configData.connectionName || '');
            setValue('environment', (configData.environment as 'test' | 'prod' | 'dev') || 'prod');
            setValue('ashost', configData.config?.ashost || '');
            setValue('sysnr', configData.config?.sysnr || '00');
            setValue('client', configData.config?.client || '800');
            setValue('lang', configData.config?.lang || 'EN');
            setValue('user', configData.authFields?.rfc_basic?.user || '');
            setValue('password', configData.authFields?.rfc_basic?.password || '');
            setValue('poolMax', configData.config?.poolMax || 10);
        }
    }, [connectors, configId]);

    const onSubmit = async (data: SapRfcForm) => {
        if (!data.environment) {
            setError('environment', { type: 'manual', message: 'Environment is required' });
            toast.error('Please select an environment');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                connectionName: data.name,
                environment: data.environment,
                authType: 'rfc_basic',
                config: {
                    ashost: data.ashost,
                    sysnr: data.sysnr,
                    client: data.client,
                    lang: data.lang,
                    poolMax: data.poolMax,
                },
                authFields: {
                    rfc_basic: {
                        user: data.user,
                        password: data.password,
                    },
                },
                userId: user?.id!,
            };

            let response;
            if (!config) response = await configApi.post(payload);
            else response = await configApi.put(config.id, payload);

            setConfig(response);
            updateConnection(response);
            toast.success('Configuration saved successfully!');
        } catch (error: any) {
            toast.error(`Failed to save configuration: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const testConnection = async () => {
        if (!config?.id) {
            toast.error('Please save configuration first');
            return;
        }

        const formData = watch();
        const connectionData = {
            connectionName: formData.name,
            environment: formData.environment,
            authType: 'rfc_basic',
            config: {
                ashost: formData.ashost,
                sysnr: formData.sysnr,
                client: formData.client,
                lang: formData.lang,
                poolMax: formData.poolMax,
            },
            authFields: {
                rfc_basic: {
                    user: formData.user,
                    password: formData.password,
                },
            },
        };

        await testConnectionApi(config.id, setTesting, connectionData, user);
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h1>SAP RFC Connection</h1>
                    <p>Configure your SAP RFC / BAPI connection</p>
                </div>
                <div className={styles.headerActions}>
                    <button
                        type="button"
                        onClick={goBack}
                        className={styles.cancelBtn}
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={testConnection}
                        disabled={testing || !config}
                        className={styles.saveBtn}
                    >
                        {testing ? 'Testing...' : 'Test Connection'}
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit(onSubmit)}
                        disabled={saving}
                        className={styles.saveBtn}
                    >
                        {saving ? 'Saving...' : 'Save Configuration'}
                    </button>
                </div>
            </div>

            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                {/* BASIC SETTINGS */}
                <Card className={styles.section}>
                    <CardHeader>
                        <CardTitle>Basic Settings</CardTitle>
                        <CardDescription>Connection name and environment.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="name"
                                label="Connection Name *"
                                value={watch('name') || ''}
                                onChange={(value) => setValue('name', value)}
                                required
                                className={styles.input}
                                externalError={errors.name?.message}
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>Environment *</label>
                            <select
                                value={watch('environment') || ''}
                                onChange={(e) => {
                                    setValue('environment', e.target.value as 'test' | 'prod' | 'dev');
                                    setError('environment', { type: 'manual', message: '' });
                                }}
                                className={`${styles.select} ${errors.environment ? styles.selectError : ''}`}
                            >
                                <option value="">Select environment</option>
                                <option value="prod">Prod</option>
                                <option value="test">Test</option>
                                <option value="dev">Dev</option>
                            </select>
                            {errors.environment && (
                                <span className={styles.error}>{errors.environment.message}</span>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* SERVER SETTINGS */}
                <Card className={styles.section}>
                    <CardHeader>
                        <CardTitle>SAP System Settings</CardTitle>
                        <CardDescription>Application server host, system number, client, and language.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="text"
                                label="Application Server Host (ashost) *"
                                value={watch('ashost') || ''}
                                onChange={(value) => setValue('ashost', value)}
                                placeholder="sap.example.com or 10.0.0.1"
                                required
                                className={styles.input}
                                externalError={errors.ashost?.message}
                            />
                        </div>
                        <div className={styles.fieldRow}>
                            <SecureInput
                                fieldType="text"
                                label="System Number (sysnr) *"
                                value={watch('sysnr') || ''}
                                onChange={(value) => setValue('sysnr', value)}
                                placeholder="00"
                                required
                                className={styles.input}
                                externalError={errors.sysnr?.message}
                            />
                            <SecureInput
                                fieldType="text"
                                label="Client *"
                                value={watch('client') || ''}
                                onChange={(value) => setValue('client', value)}
                                placeholder="800"
                                required
                                className={styles.input}
                                externalError={errors.client?.message}
                            />
                            <SecureInput
                                fieldType="text"
                                label="Language"
                                value={watch('lang') || 'EN'}
                                onChange={(value) => setValue('lang', value)}
                                placeholder="EN"
                                className={styles.input}
                                externalError={errors.lang?.message}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* AUTHENTICATION */}
                <Card className={styles.section}>
                    <CardHeader>
                        <CardTitle>Authentication</CardTitle>
                        <CardDescription>SAP RFC user credentials.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="text"
                                label="Username *"
                                value={watch('user') || ''}
                                onChange={(value) => setValue('user', value)}
                                placeholder="RFC_USER"
                                required
                                className={styles.input}
                                externalError={errors.user?.message}
                            />
                        </div>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="password"
                                label="Password *"
                                value={watch('password') || ''}
                                onChange={(value) => setValue('password', value)}
                                required
                                className={styles.input}
                                externalError={errors.password?.message}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* POOL SETTINGS */}
                <Card className={styles.section}>
                    <CardHeader>
                        <CardTitle>Connection Pool</CardTitle>
                        <CardDescription>Maximum number of concurrent RFC connections.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="text"
                                label="Pool Max Connections"
                                value={String(watch('poolMax') || 10)}
                                onChange={(value) => setValue('poolMax', parseInt(value, 10) || 10)}
                                placeholder="10"
                                className={styles.input}
                                externalError={errors.poolMax?.message}
                            />
                        </div>
                    </CardContent>
                </Card>
            </form>
        </div>
    );
}
